import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { Divider } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import ContentFooter from "../../Components/Common/ContentFooter";
import { STATE_CURRENCY } from "../../Actions/cookiesActions";
import autoAnimate from "@formkit/auto-animate";
import OptIn from "../../Components/OptIn";
import { AppContext } from "../../context";

const useStyles = createStyles((theme) => ({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: ".25rem",
    padding: 0,
    marginTop: "-1rem",
    listStyle: "none",
  },
  text: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "0.875rem",
    color: "#323232",
  },
  total: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "1.125rem",
    fontWeight: "700",
  },
  payButton: {
    marginTop: "1.5rem",
    backgroundColor: "var(--button-primary-bg-color)",
    color: "var(--button-primary-color)",
    "&:hover": {
      backgroundColor: "var(--button-primary-hover-bg-color)",
    },
  },
  optIn: {
    marginTop: ".5rem",
    marginBottom: "-1rem",
  },
}));

const calculatePrice = (price1, price2) => {
  const currencySymbols = {
    $: 1,
    "€": 1,
    "£": 1,
    kr: 1,
  };

  const symbol1 = Object.keys(currencySymbols).find((symbol) =>
    price1.includes(symbol)
  );

  if (!symbol1) {
    throw new Error("Invalid currency symbol");
  }

  const price1Value = parseFloat(
    price1.replace(symbol1, "").replace(",", ".").trim()
  );

  const difference = (price1Value - price2).toFixed(2);
  return difference.toString().replace(".", ",");
};

const CartListFooter = (props) => {
  const {
    sale,
    invalid,
    noItems,
    paymentMethods,
    deliveryMethod,
    deliveryMethods,
    providesResponsible,
    setIsOnePageCheckoutActive,
    responsibleData,
    enableOptIn,
  } = props;
  const { language, currency } = useSelector((state) => ({
    language: state.language,
    currency: state.cookies[STATE_CURRENCY],
  }));
  const { classes } = useStyles();
  const { state } = useContext(AppContext);
  const parent = useRef(null);
  const discounts = sale.discounts?.[0]?.discountInclVat;
  const filterSale = sale.payments.filter(
    (el) => el.paymentMethodName === "Presentkort"
  )?.[0];

  const fees = sale.items.filter((item) => item.article.isFee);

  const getGiftCard = useMemo(() => {
    const filterContext = state.giftCertificate;
    if (filterSale) {
      return filterSale?.amount.replace(/[^0-9.]/g, "");
    } else if (filterContext) {
      return filterContext.availableAmountInCurrency / 100;
    }
  }, [state, sale]);

  const sum = useMemo(() => {
    const calc = parseFloat(
      calculatePrice(sale?.sumInclVat, getGiftCard).replace(",", ".")
    );

    const trimSum = /[^0-9.,]+/g;
    if (getGiftCard && calc > 0) {
      return calc;
    } else if (getGiftCard && calc <= 0) {
      return "0";
    } else {
      return sale.sumInclVat.replace(trimSum, "");
    }
  }, [sale, getGiftCard]);

  const totalPaySum = useMemo(() => {
    if (currency === "SEK") return `${sum} kr`;
    if (currency === "DKK") return `${sum} kr`;
    if (currency === "NOK") return `${sum} kr`;
    if (currency === "GBP") return `£${sum}`;
    if (currency === "USD") return `$${sum}`;
    if (currency === "EUR") return `€${sum}`;
  }, [sum, currency]);

  const total = useCallback(
    (price) => {
      if (currency === "SEK") return `${price} kr`;
      if (currency === "DKK") return `${price} kr`;
      if (currency === "NOK") return `${price} kr`;
      if (currency === "GBP") return `£${price}`;
      if (currency === "USD") return `$${price}`;
      if (currency === "EUR") return `€${price}`;
    },
    [sum, currency]
  );

  useEffect(() => {
    parent.current && autoAnimate(parent.current);
  }, [parent]);

  return (
    <div>
      <ul className={classes.list} ref={parent}>
        <li className={classes.text}>
          <span>{language.Sum_Excl_Vat}</span>
          <span>{sale.sumExclVat}</span>
        </li>
        <li className={classes.text}>
          <span>{language.Sum_Incl_Vat}</span>
          <span>{sale.sumInclVat}</span>
        </li>
        {fees.map((el, i) => (
          <li key={i} className={classes.text}>
            <span>{language[el.type]}</span>
            <span>{el.totalPriceInclVat}</span>
          </li>
        ))}
        {sale.discounts.length > 0 && (
          <li className={classes.text}>
            <span>{language.Cart_Discount}</span>
            <span>-{discounts}</span>
          </li>
        )}

        {getGiftCard && (
          <li className={classes.text}>
            <span>{language.GiftCertificate}</span>
            <span>
              -
              {total(
                getGiftCard > sale.amount / 100
                  ? sale.amount / 100
                  : getGiftCard
              )}
            </span>
          </li>
        )}
      </ul>
      <Divider my="lg" style={{ borderTopColor: "#E7E9EC" }} />
      <div className={`${classes.total} total`}>
        <span>{language.Total}</span>
        <span>{totalPaySum}</span>
      </div>
      {enableOptIn && (
        <div className={classes.optIn}>
          <OptIn />
        </div>
      )}

      <ContentFooter
        invalid={invalid}
        noItems={noItems}
        sale={sale}
        deliveryMethods={deliveryMethods}
        deliveryMethod={deliveryMethod}
        paymentMethods={paymentMethods}
        providesResponsible={providesResponsible}
        responsibleData={responsibleData}
        setIsOnePageCheckoutActive={setIsOnePageCheckoutActive}
      />
    </div>
  );
};

export default CartListFooter;
